/**
 * Every /channels/:platform box's state from the page's reads and filters, in
 * one pure function (spec §3, §5), and the connected rules (R6). The page,
 * the CSV and the chat all read this view, so the three cannot disagree.
 *
 * Filters apply here with no read (spec §2.2), in one order: currency →
 * objective → campaign type / campaign. The Active filter (A) then narrows
 * box 5 and the naming coverage only: boxes 2–4 count every baht the other
 * filters keep, so box 2 is D1's box 11 row (NFR-01). A failed read fails
 * the boxes it feeds and no other (R7–R9); NULL never becomes 0; filters
 * that leave nothing give "empty", never E (box 5's ad tabs aside: no ad row
 * beside a positive ad-set spend reads 0% covered, R17). Pure.
 *
 * @module services/channels/channelView
 */
import type { ChannelFreshnessState } from '@/hooks/useLookerChannelFreshness';
import { currencyChoiceOf, filterFacts } from '@/services/overview/overviewFilters';
import { totalsOf } from '@/services/overview/overviewMath';
import type { BoxState, CurrencyChoice, ReadState } from '@/services/overview/overviewTypes';
import { rowsIn } from '@/services/overview/overviewWindows';
import { scorecardsOf, type Scorecard } from '@/services/overview/scorecards';
import { trendOf, type Trend } from '@/services/overview/spendTrend';
import {
  campaignTableOf, creativeTableOf, formatTableOf, hiddenCountOf, namingCoverageOf, targetingTableOf,
  type BreakdownContext,
} from './channelBreakdown';
import {
  activeRows, appliedFiltersOf, campaignIndexOf, campaignMatcherOf, effectiveActiveOf, filterChannelRows,
  filteredCampaignCountOf, filterOptionsOf, type RowFilters,
} from './channelFilters';
import { sumOf } from './channelMath';
import { ga4PointsOf, tilesOf } from './platformTiles';
import {
  AD_CHANNELS, isAdChannel,
  type AdChannel, type AppliedFilters, type BreakdownView, type CampaignInfo, type ChannelAdRow,
  type ChannelConnections, type ChannelEmpty, type ChannelEntity, type ChannelFactPair, type ChannelPlatform,
  type ChannelView, type ChannelViewInput, type FilterOptions, type Ga4View, type PlatformTile, type TileRows,
} from './channelTypes';

const LOADING = { status: 'loading' } as const;
const FAILED = { status: 'failed' } as const;
const EMPTY = { status: 'empty' } as const;
const ready = <T>(data: T): BoxState<T> => ({ status: 'ready', data });
const noFilters = (): AppliedFilters => ({ types: [], campaigns: [] });
const everyCampaign = (): boolean => true;

/** D1 R16: rows the filters left empty give "empty", never a box of zeros. Box 5's ad tabs are the one exception (R17, adTabsOf). */
const boxOf = <T>(rows: readonly unknown[], build: () => T): BoxState<T> =>
  (rows.length === 0 ? EMPTY : ready(build()));

/** The newest of some ISO dates; null when none is known. */
const latestOf = (dates: ReadonlyArray<string | null>): string | null =>
  dates.reduce<string | null>((max, d) => (d !== null && (max === null || d > max) ? d : max), null);

const latestFactDateOf = (facts: ReadState<ChannelFactPair>): string | null =>
  (facts.status === 'ready' ? latestOf(facts.data.current.map((r) => r.date)) : null);

// ---------------------------------------------------------------- the entities' part (R8, R11)

/** The ready entities, keyed for the breakdown tables: one entry per campaign, one per ad set. */
interface Catalog {
  index: ReadonlyMap<string, CampaignInfo>;
  adSets: ReadonlyMap<string, ChannelEntity>;
}

interface EntityScope {
  /** null until the entities are ready. */
  catalog: Catalog | null;
  options: BoxState<FilterOptions>;
  applied: AppliedFilters;
  matches: (campaignId: string) => boolean;
  /** R8: a type or campaign filter is set and the entities that validate it have not arrived. */
  waits: boolean;
}

const entityScopeOf = (input: ChannelViewInput): EntityScope => {
  const { entities, filters } = input;
  if (entities.status !== 'ready') {
    const failed = entities.status === 'failed';
    // R8: a failed read applies no type or campaign filter; a pending one holds boxes 2–4 while one is set.
    return {
      catalog: null, options: failed ? FAILED : LOADING, applied: noFilters(), matches: everyCampaign,
      waits: !failed && (filters.types.length > 0 || filters.campaigns.length > 0),
    };
  }
  const index = campaignIndexOf(entities.data, input.yesterday);
  const options = filterOptionsOf(index, input.workspaceNames, input.allWorkspaces);
  const applied = appliedFiltersOf(filters, options);
  return {
    catalog: { index, adSets: new Map(entities.data.map((e) => [e.adSetId, e] as const)) },
    options: ready(options), applied, matches: campaignMatcherOf(applied, index), waits: false,
  };
};

/**
 * FR-X-02, R5, rules-04 B4 (D1's rule): the facts' currencies decide. The
 * requested currency (URL or memory) when the channel spent in it; else D1's,
 * currencyChoiceOf over every ad channel's facts (`adSpend`), when the channel
 * spent in it; else the channel's own most spent. adSpend is read only for a
 * channel that spent in more than one currency (useChannelData): with one, D1's
 * is that one or one the channel lacks, so the channel's own is the answer
 * either way. The ads decide only when the facts failed (Ad format and
 * Creative still render then). null while the facts load, after they failed
 * while the ads are not ready, and while adSpend loads for a channel with
 * several currencies: the boxes wait rather than show a currency about to
 * change. Never written back to the URL or the memory.
 */
const moneyOf = (input: ChannelViewInput): CurrencyChoice | null => {
  const { facts, ads, adSpend, filters } = input;
  if (facts.status === 'ready') {
    const own = currencyChoiceOf(facts.data.current, filters.currency);
    const requestedHere = filters.currency !== null && own.currencies.includes(filters.currency);
    if (own.currencies.length < 2 || requestedHere) return own;
    if (adSpend.status === 'loading') return null; // wait: the boxes must not show a currency about to change
    if (adSpend.status !== 'ready') return own; // idle or failed: the channel's own, as before B4
    return currencyChoiceOf(facts.data.current, currencyChoiceOf(adSpend.data, filters.currency).currency);
  }
  if (facts.status === 'failed' && ads.status === 'ready') return currencyChoiceOf(ads.data, filters.currency);
  return null;
};

// ---------------------------------------------------------------- boxes 2–4

interface FactBoxes {
  scorecards: BoxState<Scorecard[]>;
  tiles: BoxState<PlatformTile[]>;
  trend: BoxState<Trend>;
}

/** Boxes 2–4 (R8, R9). A never touches them (spec §4), so box 2 equals D1's box 11 row (NFR-01). */
const factBoxesOf = (input: ChannelViewInput, f: RowFilters, waits: boolean): FactBoxes => {
  const { facts, windows, platform } = input;
  if (facts.status === 'failed') return { scorecards: FAILED, tiles: FAILED, trend: FAILED };
  if (facts.status !== 'ready' || waits) return { scorecards: LOADING, tiles: LOADING, trend: LOADING };
  const current = filterChannelRows(facts.data.current, f);
  const previous = filterChannelRows(facts.data.previous, f);
  // D1 R1: the cut windows are client filters of the two reads.
  const rows: TileRows = {
    current, previous, cut: rowsIn(current, windows.cut), cutPrevious: rowsIn(previous, windows.cutPrevious),
  };
  // rules-04 H2: the objectives with current rows under the type and campaign filters, before the currency filter, so a
  // tile says "no <objective> campaigns" only when there are none in any currency. The objective filter adds nothing:
  // the tiles ask only about the objective they name, which is the selected one when one is.
  const objectivesInScope = new Set(facts.data.current.filter((r) => f.matches(r.campaignId)).map((r) => r.objective));
  return {
    scorecards: boxOf(current, () => scorecardsOf(totalsOf(current), totalsOf(previous))),
    tiles: boxOf(current, () => tilesOf(platform, rows, f.objective, objectivesInScope)),
    trend: boxOf(current, () => trendOf(current, windows.current)),
  };
};

// ---------------------------------------------------------------- box 5

/** The rows box 5 shows: A applied or not (R16). */
type Shown = <R extends Pick<ChannelAdRow, 'campaignId'>>(rows: readonly R[]) => R[];

interface FactTabs extends Pick<BreakdownView, 'hidden' | 'namingCoverage' | 'campaign' | 'targeting'> {
  /** Σ ad-set spend of the shown rows, the ad-grain coverage's denominator (R17); null = the facts are not ready, or no shown row reports spend. */
  spend: number | null;
}

/** Box 5's facts half: Campaign, Targeting, the hidden count and the naming coverage (R9, R16). */
const factTabsOf = (input: ChannelViewInput, f: RowFilters, catalog: Catalog, shown: Shown): FactTabs => {
  const { facts } = input;
  if (facts.status !== 'ready') {
    const state = facts.status === 'failed' ? FAILED : LOADING;
    return { hidden: null, namingCoverage: null, campaign: state, targeting: state, spend: null };
  }
  const filtered = filterChannelRows(facts.data.current, f);
  const rows = shown(filtered);
  const ctx: BreakdownContext = {
    platform: input.platform, index: catalog.index, workspaceNames: input.workspaceNames, allWorkspaces: input.allWorkspaces,
  };
  const campaign = campaignTableOf(rows, ctx);
  return {
    // Counted before A: the non-Active campaigns with rows under the other filters.
    hidden: hiddenCountOf(filtered, catalog.index),
    namingCoverage: namingCoverageOf(campaign.rows),
    campaign: boxOf(rows, () => campaign),
    targeting: boxOf(rows, () => targetingTableOf(rows, catalog.adSets, input.platform)),
    spend: sumOf(rows, (r) => r.spend),
  };
};

/**
 * Box 5's ads half: only these fail with the ads (spec §5); same currency, filters and A as the facts (R17).
 * With ad rows the tabs are their tables. With none, the shown ad-set spend decides (spec §2.1, R17): when it is
 * positive the ads cover none of it (Google PMax has no ads), so the tabs stay ready with no rows and their
 * coverage says 0%, a measured nothing the page must show; otherwise (the facts failed, or no positive ad-set
 * spend) there is nothing to explain and they read "empty". While the facts load, `money` is null and the tabs
 * wait above, because the ad-set spend that tells the two apart is not known yet.
 */
const adTabsOf = (
  input: ChannelViewInput, f: RowFilters, money: CurrencyChoice | null, shown: Shown, factSpend: number | null,
): Pick<BreakdownView, 'format' | 'creative'> => {
  const { ads } = input;
  if (ads.status === 'failed') return { format: FAILED, creative: FAILED };
  if (ads.status !== 'ready' || money === null) return { format: LOADING, creative: LOADING };
  const rows = shown(filterChannelRows(ads.data, f));
  const adSetsSpent = factSpend !== null && factSpend > 0;
  if (rows.length === 0 && !adSetsSpent) return { format: EMPTY, creative: EMPTY };
  return { format: ready(formatTableOf(rows, factSpend)), creative: ready(creativeTableOf(rows, factSpend)) };
};

/**
 * Box 5 fails whole with the entities: names, state and A come from them (R8).
 * Once they are ready it stays drawn, each tab with its own state, so the A
 * control remains reachable even when A hides every row. It waits while the
 * page currency does (rules-04 B4): its fact tabs would read "empty" in none.
 */
const breakdownOf = (
  input: ChannelViewInput, scope: EntityScope, f: RowFilters, money: CurrencyChoice | null, currencyWaits: boolean,
): BoxState<BreakdownView> => {
  if (input.entities.status === 'failed') return FAILED;
  if (scope.catalog === null || currencyWaits) return LOADING;
  const catalog = scope.catalog;
  const activeOnly = effectiveActiveOf(input.filters.active, scope.applied, catalog.index);
  const shown: Shown = (rows) => (activeOnly ? activeRows(rows, catalog.index) : [...rows]);
  const { spend, ...factTabs } = factTabsOf(input, f, catalog, shown);
  return ready({ activeOnly, ...factTabs, ...adTabsOf(input, f, money, shown, spend) });
};

// ---------------------------------------------------------------- E (R7)

/**
 * E replaces rows 2–5: notConnected whenever the channel is known not to be
 * connected; noSpend when no current-window row of the channel, before any
 * page filter, holds spend > 0. GA4 rows carry no spend (cost 0 on every
 * local row), so GA4 shows E only when not connected.
 */
const emptyOf = (input: ChannelViewInput): ChannelEmpty | null => {
  if (input.connected === false) return { kind: 'notConnected' };
  const { facts, entities } = input;
  if (input.platform === 'analytics' || facts.status !== 'ready') return null;
  if (facts.data.current.some((r) => r.spend !== null && r.spend > 0)) return null;
  // The date comes from the entities: unknown while they load or after they fail.
  const lastSpendDate = entities.status === 'ready' ? latestOf(entities.data.map((e) => e.lastSpendDate)) : null;
  return { kind: 'noSpend', lastSpendDate };
};

// ---------------------------------------------------------------- the two page shapes

const adChannelViewOf = (input: ChannelViewInput): ChannelView => {
  const scope = entityScopeOf(input);
  const money = moneyOf(input);
  // The facts are in but D1's currency is not yet (rules-04 B4): with no currency every row is filtered out, so the
  // boxes that filter by it wait instead of reading "empty".
  const currencyWaits = input.facts.status === 'ready' && money === null;
  // currency → objective → type / campaign; A comes later, inside box 5.
  const f: RowFilters = { currency: money?.currency ?? null, objective: input.filters.objective, matches: scope.matches };
  const empty = emptyOf(input);
  return {
    // Under E no figure shows, so nothing flags the unknown currency, as on D1 and D4 (rules-04 H review F1, I2).
    money: empty !== null && money !== null ? { ...money, unknownExcluded: false } : money,
    latestFactDate: latestFactDateOf(input.facts),
    empty,
    options: scope.options,
    applied: scope.applied,
    filteredCampaigns: scope.catalog ? filteredCampaignCountOf(scope.applied, scope.catalog.index) : null,
    ...factBoxesOf(input, f, scope.waits || currencyWaits),
    breakdown: breakdownOf(input, scope, f, money, currencyWaits),
    ga4: null,
  };
};

/**
 * The GA4 panel (R15). GA rows are site-wide: the objective, the currency and
 * the cut never apply to them. The ad-spend line takes the objective and the
 * page currency. Either read failing fails this panel and nothing else (spec §5).
 */
const ga4Of = (input: ChannelViewInput, money: CurrencyChoice | null): BoxState<Ga4View> => {
  const { facts, adSpend, windows } = input;
  if (facts.status === 'failed' || adSpend.status === 'failed') return FAILED;
  if (facts.status !== 'ready' || adSpend.status !== 'ready') return LOADING;
  const { current, previous } = facts.data;
  // get_overview_facts returns no analytics rows (migration 121): every row is ad spend.
  const spend = filterFacts(adSpend.data, { channels: [], objective: input.filters.objective, currency: money?.currency ?? null });
  // No cut on GA4: FR-X-01's lag is the ad platforms' attribution, so the "cut" windows are the full ones.
  const rows: TileRows = { current, previous, cut: current, cutPrevious: previous };
  // Nor an objective: GA rows are site-wide, so the tiles read them all whatever the filter says.
  return boxOf(current, () => ({ tiles: tilesOf('analytics', rows, 'all'), ...ga4PointsOf(current, spend, windows.current) }));
};

/** The GA4 tab: its panel only; the ad-channel boxes are "empty" and never drawn there. */
const ga4ViewOf = (input: ChannelViewInput): ChannelView => {
  const { adSpend, filters } = input;
  const money = adSpend.status === 'ready' ? currencyChoiceOf(adSpend.data, filters.currency) : null;
  const empty = emptyOf(input);
  return {
    // As on the ad tabs: under E nothing flags the unknown currency (rules-04 H review F1, G1, I2).
    money: empty !== null && money !== null ? { ...money, unknownExcluded: false } : money,
    latestFactDate: latestFactDateOf(input.facts),
    empty,
    options: EMPTY,
    applied: noFilters(),
    filteredCampaigns: null,
    scorecards: EMPTY,
    tiles: EMPTY,
    trend: EMPTY,
    breakdown: EMPTY,
    ga4: ga4Of(input, money),
  };
};

export const buildChannelView = (input: ChannelViewInput): ChannelView =>
  (input.platform === 'analytics' ? ga4ViewOf(input) : adChannelViewOf(input));

// ---------------------------------------------------------------- connected (R6)

/**
 * One workspace: connected when its account statuses list the platform
 * (analytics included). All workspaces (statuses are workspace-bound): an ad
 * channel is connected when it has entities; GA4 stays unknown there (its
 * tab is hidden). null = unknown, which never says "ยังไม่ได้เชื่อมต่อ".
 */
export const connectedOf = (
  platform: ChannelPlatform,
  connections: ReadState<ChannelConnections>,
  entities: ReadState<ChannelEntity[]>,
  allWorkspaces: boolean,
): boolean | null => {
  if (allWorkspaces) {
    if (platform === 'analytics' || entities.status !== 'ready') return null;
    return entities.data.length > 0;
  }
  return connections.status === 'ready' ? connections.data.platforms.includes(platform) : null;
};

/**
 * D1 and D4 (rules-04 G3): connectedOf's one-workspace half for the one ticked ad channel, over the page's own
 * statuses read (loadChannelFreshness: the same fetchSyncStatuses read as loadChannelConnections, whose ad channels
 * it lists). null = unknown: several channels or none ticked, all workspaces (statuses are workspace-bound), or the
 * read loading, failed or not made. Unknown never says "ยังไม่ได้เชื่อมต่อ".
 */
export const soleChannelConnectedOf = (
  channels: readonly string[],
  freshness: ChannelFreshnessState,
  allWorkspaces: boolean,
): boolean | null => {
  if (allWorkspaces || channels.length !== 1 || !isAdChannel(channels[0]) || freshness.status !== 'ready') return null;
  return freshness.channels.some((c) => c.platform === channels[0]);
};

/** The GA4 tab: one workspace whose statuses list an analytics account; never all workspaces. */
export const ga4TabShownOf = (connections: ReadState<ChannelConnections>, allWorkspaces: boolean): boolean =>
  !allWorkspaces && connections.status === 'ready' && connections.data.platforms.includes('analytics');

/**
 * /channels opens the first connected ad channel in tab order; Facebook when
 * none is, when the statuses failed, and in all workspaces. null = still reading.
 */
export const defaultChannelOf = (connections: ReadState<ChannelConnections>, allWorkspaces: boolean): AdChannel | null => {
  if (allWorkspaces || connections.status === 'failed') return 'facebook';
  if (connections.status !== 'ready') return null;
  const { platforms } = connections.data;
  return AD_CHANNELS.find((p) => platforms.includes(p)) ?? 'facebook';
};
